const CATEGORY_RULE_TYPES = ['material_category', 'mixture_category']

function normalizeIds(value) {
    if (!Array.isArray(value)) {
        return []
    }

    return value
        .map(id => Number(id))
        .filter(id => Number.isFinite(id))
}

function normalizeStatement(statement) {
    if (statement && typeof statement === 'object') {
        const statementValue = statement.statement && typeof statement.statement === 'object'
            ? statement.statement.id
            : statement.statement

        return {
            id: Number(statement.id || statementValue),
            coveredExcluded: statement.coveredExcluded,
        }
    }

    return {
        id: Number(statement),
        coveredExcluded: undefined,
    }
}

function isSameId(left, right) {
    return Number(left) === Number(right)
}

function getSelectedCategoryIds(type, productContext) {
    if (type === 'material_category') {
        return normalizeIds(productContext.materialCategoryIds)
    }

    if (type === 'mixture_category') {
        return normalizeIds(productContext.mixtureCategoryIds)
    }

    return []
}

function getCategoryRelations(type, productContext) {
    if (type === 'material_category') {
        return productContext.materialCategoryRelations || {}
    }

    if (type === 'mixture_category') {
        return productContext.mixtureCategoryRelations || {}
    }

    return {}
}

function matchesGeneralProductStatement(rule, productContext) {
    const statements = Array.isArray(productContext.regulatedProductStatements)
        ? productContext.regulatedProductStatements.map(normalizeStatement)
        : normalizeIds(productContext.generalProductStatementIds).map(id => ({ id }))

    return statements.some(statement => {
        if (!isSameId(statement.id, rule.id)) {
            return false
        }

        if (!Object.prototype.hasOwnProperty.call(rule, 'coveredExcluded')) {
            return true
        }

        return statement.coveredExcluded === rule.coveredExcluded
    })
}

function matchesCategoryWithMap(params, relationMapName) {
    if (isSameId(params.selectedId, params.ruleId)) {
        return true
    }

    const relations = getCategoryRelations(params.type, params.productContext)
    const relationIds = normalizeIds(relations[relationMapName]?.[params.ruleId])

    return relationIds.some(id => isSameId(id, params.selectedId))
}

export default class SubstanceListVisibilityRuleEvaluator {
    constructor(options = {}) {
        this.categoryMatcher = options.categoryMatcher || null
    }

    matches(rule, productContext = {}) {
        if (!rule) {
            return true
        }

        if (Array.isArray(rule.all)) {
            return rule.all.every(nestedRule => this.matches(nestedRule, productContext))
        }

        if (Array.isArray(rule.any)) {
            return rule.any.some(nestedRule => this.matches(nestedRule, productContext))
        }

        if (rule.type === 'general_product_statement') {
            return matchesGeneralProductStatement(rule, productContext)
        }

        if (CATEGORY_RULE_TYPES.includes(rule.type)) {
            return this.matchesCategory(rule, productContext)
        }

        return false
    }

    matchesCategory(rule, productContext) {
        const selectedIds = getSelectedCategoryIds(rule.type, productContext)
        const match = rule.match || 'self'

        return selectedIds.some(selectedId => this.matchesCategoryId({
            type: rule.type,
            selectedId,
            ruleId: rule.id,
            match,
            productContext,
        }))
    }

    matchesCategoryId(params) {
        if (params.match === 'self') {
            return isSameId(params.selectedId, params.ruleId)
        }

        if (isSameId(params.selectedId, params.ruleId)) {
            return true
        }

        if (this.categoryMatcher) {
            return this.categoryMatcher(params)
        }

        if (params.match === 'self_or_descendant') {
            return matchesCategoryWithMap(params, 'descendantsById')
        }

        if (params.match === 'self_or_ancestor') {
            return matchesCategoryWithMap(params, 'ancestorsById')
        }

        return false
    }
}
